// SessionExpiryHandler.tsx
import React, { useEffect, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';

interface SessionExpiryHandlerProps {
  children: ReactNode;
}

const SessionExpiryHandler: React.FC<SessionExpiryHandlerProps> = ({ children }) => {
  const { isLoading, signOut } = useAuth();
  const { addToast } = useToast();
  
  useEffect(() => {
    // Function to check token validity
    const checkSession = async () => {
      try {
        // Get the ID token from local storage
        const idToken = localStorage.getItem('idToken');
        
        if (!idToken) {
          return; // No need to check if there's no token
        }
        
        // Decode the token to check expiration
        const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        // If token is expired
        if (idTokenPayload.exp < currentTime) {
          // Show toast notification
          addToast({
            title: "Session Expired",
            description: "Your session has expired. Please sign in again.",
            variant: "destructive",
            duration: 5000,
          });
          
          // Sign out and redirect to login page
          await signOut();
          window.location.href = '/marketplace/login';
        }
      } catch (error) {
        console.error('Error checking session expiry:', error);
        // If there's an error parsing or checking the token, assume it's invalid
        addToast({
          title: "Authentication Error",
          description: "There was a problem with your session. Please sign in again.",
          variant: "destructive",
          duration: 5000,
        });
        
        await signOut();
        window.location.href = '/marketplace/login';
      }
    };

    // Check on initial component mount
    if (!isLoading) {
      checkSession();
    }

    // Set up interval to check periodically
    const intervalId = setInterval(checkSession, 60000); // Check every minute
    
    // Check on window focus/tab activation
    const handleFocus = () => {
      checkSession();
    };
    
    window.addEventListener('focus', handleFocus);
    
    // Clean up
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isLoading, addToast, signOut]);

  return <>{children}</>;
};

export default SessionExpiryHandler;